import { create } from 'zustand';
import { conversationAPI, chatAPI, fileAPI } from '../services/api';
import { Message, Conversation, FileInfo } from '../types';
import { conversationCache } from './conversationCache';

interface ConversationState {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  files: FileInfo[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;

  // Actions
  loadConversations: () => Promise<void>;
  createConversation: (title?: string) => Promise<void>;
  selectConversation: (conversationId: string) => Promise<void>;
  sendMessage: (message: string, files?: File[]) => Promise<void>;
  updateConversation: (conversationId: string, title: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  uploadFiles: (files: File[], conversationId: string) => Promise<FileInfo[]>;
  clearError: () => void;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  conversations: [],
  currentConversation: null,
  messages: [],
  files: [],
  isLoading: false,
  isSending: false,
  error: null,

  loadConversations: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await conversationAPI.list(50);
      set({
        conversations: response.conversations,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to load conversations',
        isLoading: false,
      });
    }
  },

  createConversation: async (title?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await conversationAPI.create(title);
      const newConversation = response.conversation;

      set((state) => ({
        conversations: [newConversation, ...state.conversations],
        currentConversation: newConversation,
        messages: [],
        files: [],
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to create conversation',
        isLoading: false,
      });
    }
  },

  selectConversation: async (conversationId: string) => {
    console.log('[DEBUG] selectConversation called with:', conversationId);
    const conversation = get().conversations.find((c) => c.id === conversationId);
    console.log('[DEBUG] Found conversation:', conversation);
    
    if (conversation) {
      const currentConversation = get().currentConversation;
      const currentMessages = get().messages;
      
      // Cache current messages before switching
      if (currentConversation && currentMessages.length > 0) {
        console.log('[DEBUG] Caching', currentMessages.length, 'messages for', currentConversation.id);
        await conversationCache.cacheMessages(currentConversation.id, currentMessages);
      }
      
      // Always set as current conversation
      set({ currentConversation: conversation, isLoading: true });
      
      // First try to load from cache
      const cachedMessages = await conversationCache.getCachedMessages(conversationId);
      console.log('[DEBUG] Cached messages found:', cachedMessages.length);
      
      if (cachedMessages.length > 0) {
        set({ messages: cachedMessages, isLoading: false });
        
        // Refresh from server in background
        get().loadMessages(conversationId).catch(console.error);
      } else {
        // No cache, load from server
        console.log('[DEBUG] No cache, loading from server...');
        await get().loadMessages(conversationId);
      }
    }
  },

  sendMessage: async (message: string, files?: File[]) => {
    const { currentConversation } = get();
    if (!currentConversation || (!message.trim() && (!files || files.length === 0))) return;

    set({ isSending: true, error: null });

    // Upload files first if any
    let uploadedFiles: FileInfo[] = [];
    if (files && files.length > 0) {
      try {
        uploadedFiles = await get().uploadFiles(files, currentConversation.id);
      } catch (error) {
        set({ isSending: false });
        return;
      }
    }

    // Add user message to UI immediately
    const userMessage: Message = {
      id: Date.now().toString(),
      conversation_id: currentConversation.id,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      files: uploadedFiles,
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
    }));

    try {
      const response = await chatAPI.sendMessage(currentConversation.id, message);

      // Add assistant message
      const assistantMessage: Message = {
        id: response.message_id || Date.now().toString(),
        conversation_id: currentConversation.id,
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString(),
        metadata: response.metadata,
      };

      set((state) => ({
        messages: [...state.messages, assistantMessage],
        isSending: false,
      }));
      
      // Update cache with new messages
      await conversationCache.updateCache(currentConversation.id, userMessage);
      await conversationCache.updateCache(currentConversation.id, assistantMessage);

      // Update conversation in list
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === currentConversation.id
            ? {
                ...conv,
                last_message: message,
                message_count: conv.message_count + 2,
                updated_at: new Date().toISOString(),
              }
            : conv
        ),
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to send message',
        isSending: false,
      });
    }
  },

  updateConversation: async (conversationId: string, title: string) => {
    try {
      await conversationAPI.update(conversationId, { title });
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, title } : conv
        ),
        currentConversation: state.currentConversation?.id === conversationId
          ? { ...state.currentConversation, title }
          : state.currentConversation,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to update conversation',
      });
    }
  },

  deleteConversation: async (conversationId: string) => {
    try {
      await conversationAPI.delete(conversationId);
      const { currentConversation } = get();
      
      set((state) => ({
        conversations: state.conversations.filter(conv => conv.id !== conversationId),
        currentConversation: currentConversation?.id === conversationId ? null : currentConversation,
        messages: currentConversation?.id === conversationId ? [] : state.messages,
        files: currentConversation?.id === conversationId ? [] : state.files,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to delete conversation',
      });
    }
  },
  
  loadMessages: async (conversationId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await chatAPI.getMessages(conversationId);
      console.log('[DEBUG] loadMessages response:', response);
      console.log('[DEBUG] response type:', typeof response);
      console.log('[DEBUG] response.messages:', response.messages);
      
      const messages = response.messages || [];
      console.log('[DEBUG] Extracted messages:', messages);
      console.log('[DEBUG] Messages length:', messages.length);
      
      set({
        messages,
        isLoading: false,
      });
      
      // Update cache with fresh data
      if (messages.length > 0) {
        await conversationCache.cacheMessages(conversationId, messages);
      }
      
      // Load files for this conversation
      const filesResponse = await fileAPI.list(conversationId);
      set({ files: filesResponse.files || [] });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to load messages',
        isLoading: false,
      });
    }
  },
  
  uploadFiles: async (files: File[], conversationId: string) => {
    const uploadedFiles: FileInfo[] = [];
    
    for (const file of files) {
      try {
        const response = await fileAPI.upload(file, conversationId);
        uploadedFiles.push(response.file);
      } catch (error: any) {
        set({
          error: `Failed to upload ${file.name}: ${error.response?.data?.detail || 'Unknown error'}`,
        });
        throw error;
      }
    }
    
    set((state) => ({
      files: [...state.files, ...uploadedFiles],
    }));
    
    return uploadedFiles;
  },
  
  clearError: () => set({ error: null }),
}));
